import {Component, getComponent} from '@symfony/ux-live-component';
import type {Placement} from '@floating-ui/dom';
import {findLiveComponentRoot} from 'app/stimulus/live-component-render';

// Shared helpers of the `dropdown` and `popover` controllers. Their panels are native `popover="manual"`
// elements rendered in the top layer and appended to a configurable root, so a panel is usually not a DOM
// descendant of the overlay that opened it.

const panelOwners = new WeakMap<HTMLElement, HTMLElement>();

let panelIdCounter = 0;

/**
 * The id of the panel; a panel without one gets a generated id.
 */
export const ensurePanelId = (panel: HTMLElement, prefix: string): string => {
    if (panel.id === '') {
        panel.id = `${prefix}-panel-${++panelIdCounter}`;
    }
    return panel.id;
};

/**
 * Remembers which trigger opened the panel, so a click inside a nested overlay's panel counts as a click
 * inside every overlay above it in the trigger chain.
 */
export const setPanelOwner = (panel: HTMLElement, trigger: HTMLElement): void => {
    panelOwners.set(panel, trigger);
};

/**
 * True when the target is inside one of the given elements, directly or through a chain of overlay panels
 * whose triggers lie inside them.
 */
export const isInsideOverlayTree = (target: EventTarget | null, containers: HTMLElement[]): boolean => {
    let node: Node | null = target instanceof Node ? target : null;
    const visited = new Set<HTMLElement>();
    while (node !== null) {
        for (const container of containers) {
            if (container === node || container.contains(node)) {
                return true;
            }
        }
        const element: Element | null = node instanceof Element ? node : node.parentElement;
        const panel = element?.closest<HTMLElement>('[popover]') ?? null;
        if (panel === null || visited.has(panel)) {
            return false;
        }
        visited.add(panel);
        node = panelOwners.get(panel) ?? null;
    }
    return false;
};

/**
 * Detail of the `dropdown:closed` / `popover:closed` events. `afterAction` is true when the overlay closed
 * because the Live action of its close-on-click element finished.
 */
export type OverlayClosedDetail = {
    afterAction: boolean,
};

/**
 * Calls `close` when an overlay whose trigger lies inside the panel closes after its Live action. The Live
 * render does not update a panel appended by JS, so the outer overlay would otherwise stay open with stale
 * content. An overlay closed without an action (decline, Escape, outside click) leaves the outer one open.
 */
export const closeWithNestedOverlayAfterAction = (panel: HTMLElement, close: () => void, signal: AbortSignal): void => {
    const listener = (event: Event): void => {
        if (event.target === panel || !(event instanceof CustomEvent)) {
            return;
        }
        const detail = event.detail as Partial<OverlayClosedDetail> | null;
        if (detail !== null && detail.afterAction === true) {
            close();
        }
    };
    panel.addEventListener('dropdown:closed', listener, {signal});
    panel.addEventListener('popover:closed', listener, {signal});
};

/**
 * Adds the id to the space-separated id list of the attribute (e.g. `aria-describedby`).
 */
export const addIdReference = (element: HTMLElement, attribute: string, id: string): void => {
    const ids = (element.getAttribute(attribute) ?? '').split(' ').filter((value: string): boolean => value !== '');
    if (!ids.includes(id)) {
        ids.push(id);
    }
    element.setAttribute(attribute, ids.join(' '));
};

/**
 * Removes the id from the space-separated id list of the attribute; removes the attribute when it is empty.
 */
export const removeIdReference = (element: HTMLElement, attribute: string, id: string): void => {
    const ids = (element.getAttribute(attribute) ?? '').split(' ')
        .filter((value: string): boolean => value !== '' && value !== id);
    if (ids.length === 0) {
        element.removeAttribute(attribute);
        return;
    }
    element.setAttribute(attribute, ids.join(' '));
};

export const isToggleCloseEvent = (event: Event): boolean => {
    return (event as ToggleEvent).newState === 'closed';
};

export const isPopoverOpen = (panel: HTMLElement): boolean => {
    return panel.matches(':popover-open');
};

/**
 * `Element.checkVisibility()` where supported; Safari 17.0–17.3 have the Popover API but not this method.
 */
export const isElementVisible = (element: Element): boolean => {
    if (typeof element.checkVisibility === 'function') {
        return element.checkVisibility();
    }
    return element.getClientRects().length > 0;
};

/**
 * True when the click was produced by the keyboard (Enter or Space on the trigger), not by a pointer.
 */
export const isKeyboardActivation = (event: Event | null): boolean => {
    return event instanceof MouseEvent && event.detail === 0;
};

/**
 * True when the focus is inside the panel or got lost (on `body`): hiding the panel then loses the focus,
 * so it should go back to the trigger.
 */
export const isFocusInsideOrLost = (panel: HTMLElement): boolean => {
    const active = document.activeElement;
    return active === null || active === document.body || panel.contains(active);
};

export const isFocusInside = (panel: HTMLElement): boolean => {
    const active = document.activeElement;
    return active !== null && panel.contains(active);
};

export const focusWithoutScroll = (element: HTMLElement): void => {
    element.focus({preventScroll: true});
};

const menuItemSelector = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const isEnabled = (element: HTMLElement): boolean => {
    return !element.hasAttribute('disabled') && element.getAttribute('aria-disabled') !== 'true';
};

/**
 * The enabled, visible menu items of the panel in document order.
 */
export const getMenuItems = (panel: HTMLElement): HTMLElement[] => {
    return Array.from(panel.querySelectorAll<HTMLElement>(menuItemSelector))
        .filter((item: HTMLElement): boolean => isEnabled(item) && isElementVisible(item));
};

/**
 * The menu item the target is in, or null.
 */
export const findMenuItem = (target: EventTarget | null, panel: HTMLElement): HTMLElement | null => {
    if (!(target instanceof Element)) {
        return null;
    }
    const item = target.closest<HTMLElement>(menuItemSelector);
    return item !== null && panel.contains(item) ? item : null;
};

/**
 * The first `[autofocus]` element of the panel, otherwise its first focusable element, or null.
 */
export const findInitialFocus = (panel: HTMLElement): HTMLElement | null => {
    const autofocus = Array.from(panel.querySelectorAll<HTMLElement>('[autofocus]'))
        .find((element: HTMLElement): boolean => isElementVisible(element));
    if (autofocus !== undefined) {
        return autofocus;
    }
    return Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector))
        .find((element: HTMLElement): boolean => isElementVisible(element)) ?? null;
};

/**
 * True when the element takes text input, where arrow keys move the caret.
 */
export const isTextEntry = (element: EventTarget | null): boolean => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }
    return element.isContentEditable
        || element instanceof HTMLInputElement
        || element instanceof HTMLTextAreaElement
        || element instanceof HTMLSelectElement;
};

/**
 * The opposite side first, then the two perpendicular sides; the alignment is mirrored for the perpendicular
 * sides so the panel keeps growing away from the trigger.
 */
export const getFallbackPlacements = (placement: Placement): Placement[] => {
    const [side, alignment] = placement.split('-') as [string, string | undefined];
    const modifier = alignment === undefined ? '' : `-${alignment}`;
    let counterModifier = '';
    if (alignment === 'start') {
        counterModifier = '-end';
    } else if (alignment === 'end') {
        counterModifier = '-start';
    }
    if (side === 'top') {
        return [`bottom${modifier}`, `left${counterModifier}`, `right${counterModifier}`] as Placement[];
    }
    if (side === 'bottom') {
        return [`top${modifier}`, `left${counterModifier}`, `right${counterModifier}`] as Placement[];
    }
    if (side === 'left') {
        return [`right${modifier}`, `top${counterModifier}`, `bottom${counterModifier}`] as Placement[];
    }
    return [`left${modifier}`, `top${counterModifier}`, `bottom${counterModifier}`] as Placement[];
};

/**
 * Resolves the Live component the element belongs to, or null outside a Live component. Resolve it before
 * the user interaction: `getComponent()` polls, so resolving it inside a click handler would miss the
 * request the click starts.
 */
export const resolveLiveComponent = async (element: HTMLElement): Promise<Component | null> => {
    const root = findLiveComponentRoot(element);
    if (root === null) {
        return null;
    }
    try {
        return await getComponent(root);
    } catch (error) {
        console.error(error);
        return null;
    }
};

/**
 * Resolves after the next request of the component ends: its render finished, it failed or the network
 * request was rejected. Resolves after `startTimeout` ms when no request starts (the click did not reach
 * Live, e.g. a validation prevented it). A redirect response never resolves, the page navigates away.
 */
export const waitForLiveRequest = (component: Component, startTimeout: number = 1000): Promise<void> => {
    return new Promise((resolve): void => {
        let started = false;
        let settled = false;
        const finish = (): void => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            component.off('loading.state:started', onStarted);
            component.off('render:finished', onFinished);
            component.off('response:error', onFinished);
            resolve();
        };
        const onFinished = (): void => {
            if (started) {
                finish();
            }
        };
        const onStarted = (_element: HTMLElement, request: { promise: Promise<Response> }): void => {
            if (started) {
                return;
            }
            started = true;
            clearTimeout(timer);
            request.promise.catch((): void => finish());
        };
        const timer = setTimeout((): void => {
            if (!started) {
                finish();
            }
        }, startTimeout);
        component.on('loading.state:started', onStarted);
        component.on('render:finished', onFinished);
        component.on('response:error', onFinished);
    });
};

/**
 * The element marked with the close-on-click attribute for the click target inside the panel, or null.
 */
export const findCloseOnClickElement = (target: EventTarget | null, panel: HTMLElement, attribute: string): HTMLElement | null => {
    if (!(target instanceof Element)) {
        return null;
    }
    const marked = target.closest<HTMLElement>(`[${attribute}]`);
    if (marked === null || !panel.contains(marked)) {
        return null;
    }
    return marked;
};

/**
 * True when the click on the marked element triggers a Live action (`live#action` on the marked element or
 * on an element between it and the click target).
 */
export const triggersLiveAction = (target: EventTarget | null, marked: HTMLElement): boolean => {
    if (!(target instanceof Element)) {
        return false;
    }
    const actionElement = target.closest<HTMLElement>('[data-action*="live#action"]');
    return actionElement !== null && (actionElement === marked || marked.contains(actionElement));
};
